'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useI18n } from '@/lib/i18n';
import { WelcomeSheep } from '@/components/redesign/welcome-sheep';
import { HomeMockup } from '@/components/redesign/home-mockup';
import {
  IlluLock,
  IlluShield,
  IlluYarn,
  IlluFinished,
  IlluHome,
} from '@/components/redesign/illustrations';

/**
 * One slide of the deck. Some slides carry a left-aligned bullet list
 * (install steps, the data-safety habits, the first-steps tips) plus an
 * optional closing `footer` line; the rest are a centered art + kicker /
 * title / body. `bullets` being present is what switches a slide to the
 * left-aligned, bulleted layout.
 */
type Slide = {
  kind: 'welcome' | 'install' | 'seeyou' | 'private' | 'safety' | 'begin' | 'enjoy';
  context: 'both' | 'browser' | 'app';
  kicker: string;
  title: string;
  body: string;
  art: ReactNode;
  bullets?: string[];
  footer?: string;
};

/**
 * First-run onboarding tour (Sebastian's design, 2026-06-08).
 *
 * A skippable, full-screen presentation shown ONLY to brand-new users
 * (empty library + hasn't seen it). Its sample content is illustrative and
 * lives entirely in this component — it is NEVER written to Dexie/the synced
 * doc, so there is no demo data to sync, duplicate, or clean up. Skipping or
 * finishing drops the user into the clean, empty app. Gating + the "seen"
 * flag live in the home page (src/app/page.tsx); replay lives in Settings.
 */

const fontSerif = "'Fraunces', 'Times New Roman', serif";
const fontSans = 'var(--font-geist-sans), system-ui, sans-serif';

/**
 * Render a copy string with `**bold**` spans as <strong>. Lets each i18n
 * string stay a single translatable string while letting a screen highlight
 * the few words a user should be able to grasp without reading every word
 * (Sebastian 2026-06-13). Bold is the ONLY emphasis on these screens — the
 * bulleted slides use a flat weight otherwise so nothing outshouts the points.
 */
function withEmphasis(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} style={{ fontWeight: 700, color: 'var(--ink)' }}>
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    ),
  );
}

/**
 * Wraps a tour slide's line-art glyph (Sebastian 2026-06-13 — the deck uses
 * the app's own hand-drawn illustration family instead of emoji). Sets the
 * ink stroke colour the SVGs inherit via currentColor.
 */
const TourGlyph = ({ children }: { children: ReactNode }) => (
  <div style={{ color: 'var(--ink)', display: 'flex', lineHeight: 1 }} aria-hidden="true">
    {children}
  </div>
);

/**
 * The knit-purl rhythm trailing off the bottom of the Enjoy slide
 * (Sebastian 2026-06-13): the two words ("knit, purl" / "hladce, obratce")
 * repeat down the page, each successive word a little more transparent than
 * the last, until they fade to nothing. `loop` is the comma-separated word
 * pair from i18n; it continues the body sentence ("…and now just").
 */
function KnitPurlFade({ loop }: { loop: string }) {
  const words = loop.split(',').map((w) => w.trim()).filter(Boolean);
  if (words.length === 0) return null;
  // Enough repetitions to trail from the body down to roughly the button on a
  // phone, the linear fade reaching full transparency by the end.
  const COUNT = 48;
  return (
    <p
      aria-hidden="true"
      style={{
        margin: '4px 0 0',
        fontFamily: fontSerif,
        fontStyle: 'italic',
        fontSize: 'clamp(15px, 4vw, 17px)',
        lineHeight: 1.5,
        color: 'var(--ink-2)',
      }}
    >
      {Array.from({ length: COUNT }).map((_, i) => (
        <span key={i} style={{ opacity: Math.max(0, 1 - i / (COUNT - 1)) }}>
          {i === 0 ? ' ' : ''}
          {words[i % words.length]}
          {i < COUNT - 1 ? ', ' : ''}
        </span>
      ))}
    </p>
  );
}

const HALFTONE = {
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.06) 1px, transparent 1.4px)',
  backgroundSize: '5px 5px',
};

interface Props {
  /** Called when the user skips or finishes — set the seen flag + hide. */
  onDone: () => void;
  /**
   * Install-reminder mode (Option B, Veronika 2026-06-18): render ONLY the
   * install slide as a standalone, dismissible reminder. Shown to browser
   * (non-standalone) users every session until they install — see the gating
   * in `app/page.tsx`. Not the full welcome tour.
   */
  installOnly?: boolean;
}

export default function OnboardingTour({ onDone, installOnly = false }: Props) {
  const { t, lang, setLang } = useI18n();
  const [step, setStep] = useState(0);

  // Detect the installed / standalone PWA. The "add to home screen" screen is
  // only relevant in a browser; once the app is on the home screen it's pulled
  // out of the deck so the user never sees "you're probably in your browser"
  // when they clearly aren't (Veronika 2026-06-13). First render assumes
  // browser (false); the welcome screen is identical in both, so flipping to
  // standalone after mount causes no visible flash.
  const [isStandalone, setIsStandalone] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Testing override (Veronika 2026-06-13): the dev site opens as an
    // installed app on her phone, so she couldn't otherwise preview the
    // BROWSER tour. `?tour=browser` / `?tour=app` forces the version and is
    // remembered for this tab session so it survives the Settings → Replay
    // navigation (which drops query params); `?tour=auto` clears it.
    const fromUrl = new URLSearchParams(window.location.search).get('tour');
    if (fromUrl === 'browser' || fromUrl === 'app') {
      sessionStorage.setItem('knitlib-tour-ctx', fromUrl);
    } else if (fromUrl === 'auto') {
      sessionStorage.removeItem('knitlib-tour-ctx');
    }
    const forced = sessionStorage.getItem('knitlib-tour-ctx');
    if (forced === 'browser' || forced === 'app') {
      setIsStandalone(forced === 'app');
      return;
    }
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setIsStandalone(!!standalone);
  }, []);

  // Each slide declares where it belongs: 'both', 'browser' (only the in-
  // browser tour), or 'app' (only the installed-PWA tour). The two tours have
  // different jobs (Veronika 2026-06-13): the browser tour funnels the user
  // into installing + opening the app and ends with a goodbye; the installed
  // app runs the real onboarding (privacy + first steps).
  const allSlides: Slide[] = [
    {
      kind: 'welcome' as const,
      context: 'both' as const,
      kicker: t.tourWelcomeKicker,
      title: t.tourWelcomeTitle,
      body: t.tourWelcomeBody,
      // The welcome slide renders its own bespoke <WelcomeSheep/> field below
      // (left-aligned masthead layout), so it doesn't use this shared `art`.
      art: null,
    },
    {
      kind: 'install' as const,
      context: 'browser' as const,
      kicker: t.tourInsideKicker,
      title: t.tourInsideTitle,
      body: t.tourInsideBody,
      // Framed phone mockup of the real home — a faithful "this is your app"
      // preview, ported from the marketing site (Veronika 2026-06-13). Shrunk
      // so the whole install message + steps + Continue fit a phone without
      // scrolling (Veronika 2026-06-18).
      art: <HomeMockup width={76} />,
      // Per-platform install bullets + a closing nudge. The bullet block is
      // shared with the app-tour's data-safety / first-steps slides below.
      bullets: [t.tourInstallIos, t.tourInstallAndroid],
      footer: t.tourInstallDone,
    },
    {
      kind: 'seeyou' as const,
      context: 'browser' as const,
      kicker: '',
      title: t.tourSeeYouTitle,
      body: t.tourSeeYouBody,
      art: (
        <TourGlyph>
          <IlluHome size={60} w={1.8} />
        </TourGlyph>
      ),
    },
    {
      kind: 'private' as const,
      context: 'app' as const,
      kicker: t.tourPrivateKicker,
      title: t.tourPrivateTitle,
      body: t.tourPrivateBody,
      art: (
        <TourGlyph>
          <IlluLock size={58} w={1.8} />
        </TourGlyph>
      ),
    },
    {
      // "Where to begin" sits 3rd, before data safety (Sebastian 2026-06-13).
      kind: 'begin' as const,
      context: 'app' as const,
      kicker: t.tourBeginKicker,
      title: t.tourBeginTitle,
      body: t.tourBeginBody,
      bullets: [t.tourBeginTip1, t.tourBeginTip2, t.tourBeginTip3],
      art: (
        <TourGlyph>
          <IlluYarn size={60} w={1.8} />
        </TourGlyph>
      ),
    },
    {
      kind: 'safety' as const,
      context: 'app' as const,
      kicker: t.tourSafetyKicker,
      title: t.tourSafetyTitle,
      body: t.tourSafetyBody,
      bullets: [t.tourSafetyRule1, t.tourSafetyRule2, t.tourSafetyRule3],
      footer: t.tourSafetyFooter,
      art: (
        <TourGlyph>
          <IlluShield size={58} w={1.8} />
        </TourGlyph>
      ),
    },
    {
      kind: 'enjoy' as const,
      context: 'app' as const,
      kicker: '',
      title: t.tourEnjoyTitle,
      body: t.tourEnjoyBody,
      art: (
        <TourGlyph>
          <IlluFinished size={62} w={1.8} />
        </TourGlyph>
      ),
    },
  ];

  // Keep the slides for this context: shared screens + the ones for whichever
  // tour we're in (installed app vs browser).
  const ctx = isStandalone ? 'app' : 'browser';
  const slides = installOnly
    ? allSlides.filter((s) => s.kind === 'install')
    : allSlides.filter((s) => s.context === 'both' || s.context === ctx);

  const safeStep = Math.min(step, slides.length - 1);
  const isLast = safeStep === slides.length - 1;
  const slide = slides[safeStep];
  const isWelcome = slide.kind === 'welcome';
  // Slides with a bullet list (install steps, data-safety habits, first-
  // steps tips) render left-aligned with the bullets under the body.
  const hasBullets = !!slide.bullets?.length;
  // The install reminder carries the most copy (intro + 2 platform steps +
  // a footer) and shows on every browser launch, so it gets tighter vertical
  // rhythm to fit a small phone without scrolling (Veronika 2026-06-18).
  const compact = slide.kind === 'install';
  const next = () => (isLast ? onDone() : setStep((s) => s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div
      data-testid="onboarding-tour"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--paper-1)',
        ...HALFTONE,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
        // A bit more breathing room from the screen edges (Sebastian
        // 2026-06-13) — left-aligned text was sitting too close to the edge.
        paddingLeft: 28,
        paddingRight: 28,
      }}
    >
      {/* Top bar: step counter + Skip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {installOnly ? <span /> : (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            {String(safeStep + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
          </span>
        )}
        {!isLast && (
          <button
            type="button"
            data-testid="onboarding-skip"
            onClick={onDone}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--ink-2)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 4px',
            }}
          >
            {t.tourSkip}
          </button>
        )}
      </div>

      {/* Language toggle — on the welcome screen (so the very first thing a
          user can do is pick English / Čeština, Veronika 2026-06-13) AND on the
          standalone install reminder (installOnly), which browser users see
          without ever passing the welcome slide (Veronika 2026-06-18).
          Switching re-renders the tour (and persists the app language via
          setLang → localStorage). */}
      {(isWelcome || installOnly) && (
        // Extra gap below the toggle on the install reminder so the language
        // buttons aren't crammed against the phone mockup (Veronika 2026-06-18).
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16, marginBottom: installOnly ? 26 : 0 }}>
          <div style={{ display: 'inline-flex', border: '2px solid var(--ink)' }} role="group" aria-label="Language">
            {(['en', 'cs'] as const).map((l, i) => (
              <button
                key={l}
                type="button"
                data-testid={`onboarding-lang-${l}`}
                aria-pressed={lang === l}
                onClick={() => setLang(l)}
                style={{
                  padding: '8px 16px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  background: lang === l ? 'var(--accent)' : 'var(--paper-2)',
                  color: lang === l ? '#fff' : 'var(--ink-2)',
                  border: 'none',
                  borderLeft: i === 1 ? '2px solid var(--ink)' : 'none',
                  cursor: 'pointer',
                }}
              >
                {l === 'en' ? 'English' : 'Čeština'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Center: art + copy. The welcome screen is left-aligned with the real
          KNITTER'S / library masthead (Veronika 2026-06-13); the other screens
          keep the centered kicker/title/body layout. */}
      {isWelcome ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            textAlign: 'left',
          }}
        >
          {/* key={lang} so tapping a different language remounts the sheep and
              replays her whole walk-in (Veronika 2026-06-13 — she wants the
              animation to restart on language switch, not just stay put). */}
          <div style={{ width: '100%', marginBottom: 14 }}>
            <WelcomeSheep key={lang} />
          </div>
          <p
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
              margin: '0 0 7px',
            }}
          >
            {t.tourWelcomeKicker}
          </p>
          {/* Real app masthead — mirrors src/app/page.tsx (Inter 800 caps over
              Fraunces italic), the same lockup used on the home screen + site. */}
          <div
            style={{
              fontFamily: fontSans,
              fontWeight: 800,
              fontSize: 'clamp(32px, 10.5vw, 46px)',
              lineHeight: 0.85,
              letterSpacing: '-0.03em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
            }}
          >
            Knitter&rsquo;s
            <br />
            <span
              style={{
                fontStyle: 'italic',
                fontFamily: fontSerif,
                fontWeight: 300,
                textTransform: 'none',
                letterSpacing: '-0.02em',
              }}
            >
              library
            </span>
          </div>
          <p
            style={{
              fontFamily: fontSerif,
              fontStyle: 'italic',
              fontSize: 'clamp(13px, 3.5vw, 15px)',
              lineHeight: 1.5,
              color: 'var(--ink-2)',
              maxWidth: 420,
              margin: '16px 0 0',
            }}
          >
            {withEmphasis(t.tourWelcomeBody)}
          </p>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            // Every slide is left-aligned like the welcome screen
            // (Sebastian 2026-06-13) — icon, kicker, title and body all hang
            // off the left margin. The fixed-height deck can't grow, so allow
            // the content column to scroll on short phones rather than clip.
            alignItems: 'flex-start',
            // The Enjoy slide is top-aligned (with breathing room above, so the
            // whole block doesn't sit too high — Sebastian 2026-06-13) so its
            // knit-purl rhythm can trail down and fade toward the bottom; the
            // rest stay centered.
            justifyContent: slide.kind === 'enjoy' ? 'flex-start' : 'center',
            paddingTop: slide.kind === 'enjoy' ? '15vh' : undefined,
            textAlign: 'left',
            // Tighter on the install slide so the (long) message + steps +
            // Continue fit on a phone without scrolling (Veronika 2026-06-18).
            gap: compact ? 8 : 22,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              // Icon sits at the left margin above the left-aligned text.
              justifyContent: 'flex-start',
              // The install slide shrinks its phone mockup (constrain width →
              // its max-width:100% scales height down too) so the whole
              // message + steps + Continue fit without scrolling on a phone
              // (Veronika 2026-06-18).
              minHeight: slide.kind === 'install' ? 0 : 64,
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {slide.art}
          </div>
          <div style={{ maxWidth: 360 }}>
            {slide.kicker ? (
              <p
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--accent)',
                  margin: '0 0 10px',
                }}
              >
                {slide.kicker}
              </p>
            ) : null}
            <h2
              style={{
                fontFamily: fontSans,
                fontWeight: 800,
                fontSize: compact ? 'clamp(23px, 6vw, 28px)' : 'clamp(26px, 7vw, 34px)',
                lineHeight: 1.08,
                letterSpacing: '-0.01em',
                color: 'var(--ink)',
                margin: compact ? '0 0 8px' : '0 0 14px',
              }}
            >
              {slide.title}
            </h2>
            <p
              style={{
                // On bulleted slides the intro drops the italic-serif "voice"
                // and matches the bullets' plain sans so it doesn't read as
                // more important than the points below — they're all equal
                // (Sebastian 2026-06-13). Bold marks what to scan. Slides with
                // no list keep the editorial italic serif.
                fontFamily: hasBullets ? fontSans : fontSerif,
                fontStyle: hasBullets ? 'normal' : 'italic',
                fontSize: hasBullets ? 'clamp(14px, 3.8vw, 15.5px)' : 'clamp(15px, 4vw, 17px)',
                lineHeight: compact ? 1.4 : 1.5,
                color: hasBullets ? 'var(--ink)' : 'var(--ink-2)',
                margin: 0,
              }}
            >
              {withEmphasis(slide.body)}
            </p>
            {/* The knit-purl rhythm fading down off the Enjoy slide. */}
            {slide.kind === 'enjoy' && <KnitPurlFade loop={t.tourEnjoyLoop} />}
            {/* Left-aligned bullet list — shared by the install-steps slide
                (per-platform instructions), the data-safety habits, and the
                first-steps tips. Driven entirely by the slide's `bullets`
                with an optional closing `footer` line. */}
            {hasBullets && (
              <>
                <div
                  style={{
                    marginTop: compact ? 10 : 16,
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: compact ? 7 : 9,
                  }}
                >
                  {slide.bullets!.map((line, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <span style={{ color: 'var(--accent)', fontWeight: 800, lineHeight: 1.45 }}>•</span>
                      <span
                        style={{
                          fontFamily: fontSans,
                          // Same size/weight as the (now non-italic) intro so
                          // the intro and the points read as one equal block.
                          fontSize: 14.5,
                          lineHeight: 1.45,
                          color: 'var(--ink)',
                        }}
                      >
                        {withEmphasis(line)}
                      </span>
                    </div>
                  ))}
                </div>
                {slide.footer ? (
                  // The footer is a genuine aside (a pointer to the Manual), so
                  // it's a quiet sans footnote — clearly subordinate to the
                  // equal-weight intro + bullets above, not an italic that
                  // competes with them.
                  <p
                    style={{
                      margin: compact ? '10px 0 0' : '16px 0 0',
                      fontFamily: fontSans,
                      fontSize: 12.5,
                      lineHeight: 1.45,
                      color: 'var(--ink-3)',
                    }}
                  >
                    {withEmphasis(slide.footer)}
                  </p>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      {/* Progress dots removed (Veronika 2026-06-13) — the "NN / NN" counter
          top-left already shows which screen you're on, so the dots were
          redundant + confusing. */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        {safeStep > 0 && (
          <button
            type="button"
            data-testid="onboarding-back"
            onClick={back}
            style={{
              flexShrink: 0,
              padding: '14px 20px',
              background: 'var(--paper-2)',
              border: '2px solid var(--ink)',
              fontFamily: fontSans,
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '0.02em',
              color: 'var(--ink)',
              cursor: 'pointer',
            }}
          >
            {t.tourBack}
          </button>
        )}
        <button
          type="button"
          data-testid={isLast ? 'onboarding-start' : 'onboarding-next'}
          onClick={next}
          style={{
            flex: 1,
            padding: '14px 20px',
            // Brand-coloured fill with DARK ink text (Sebastian 2026-06-13) —
            // he disliked the white button labels; the pale palette colours
            // read well with the ink text the rest of the app's buttons use,
            // so the CTA is colourful without any white. The final "Enjoy"
            // slide's START button is yellow as a warm send-off; the rest are
            // light blue.
            background:
              slide.kind === 'enjoy' ? 'var(--accent-warm-light)' : 'var(--accent-slate)',
            color: 'var(--ink)',
            border: '2px solid var(--ink)',
            fontFamily: fontSans,
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {installOnly ? t.tourInstallContinue : isLast ? (slide.kind === 'seeyou' ? t.tourSeeYouCta : t.tourStart) : t.tourNext}
        </button>
      </div>
    </div>
  );
}
